import React, { useId, useMemo, useRef, useState } from 'react';
import plusIcon from '../../../img/plus.svg';
import type { MediaCategory } from '../../../types/checklist';
import { mediaCategoryLabel } from '../../../types/checklist';

interface MediaUploaderProps {
  category: MediaCategory;
  disabled?: boolean;
  accept?: string;
  onAddFiles: (files: File[]) => void;
  helperText?: string;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  category,
  disabled,
  accept = 'image/*,video/*',
  onAddFiles,
  helperText,
}) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const title = useMemo(() => mediaCategoryLabel[category], [category]);

  const openFileDialog = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFiles = (list: FileList | null) => {
    if (!list || disabled) return;
    const files = Array.from(list);
    if (files.length === 0) return;
    onAddFiles(files);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) onAddFiles(files);
  };

  return (
    <div className="checklist-uploader">
      <div
        className={
          isDragging
            ? 'checklist-dropzone checklist-dropzone--dragging'
            : 'checklist-dropzone'
        }
        onClick={openFileDialog}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openFileDialog();
        }}
        aria-disabled={disabled ? 'true' : 'false'}
      >
        <div className="checklist-dropzone-content">
          <div className="checklist-dropzone-title">Adicionar mídias em {title}</div>
          <div className="checklist-dropzone-subtitle">
            Arraste e solte aqui, ou clique para selecionar.
          </div>
          {helperText && <div className="checklist-dropzone-helper">{helperText}</div>}
          <button
            type="button"
            className="checklist-add-btn"
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
            onMouseDown={(e) => {
              // Evita que o clique borbulhe para a dropzone e dispare dois handlers.
              e.stopPropagation();
            }}
            disabled={disabled}
          >
            <img src={plusIcon} alt="" aria-hidden="true" className="checklist-add-btn-icon" />
            Adicionar fotos
          </button>
        </div>

        <input
          id={inputId}
          ref={inputRef}
          className="checklist-file-input"
          type="file"
          multiple
          accept={accept}
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default MediaUploader;
